/**
 * The per-render trace scope, published on `globalThis` (by `src/server.ts`) rather than exported, so
 * `head()` can read it from a module in the CLIENT bundle too. Isomorphic: no server-only imports.
 * docs/notes/portals/ssr-trace.md
 */
export interface SsrTrace {
	traceId: string;
	rootSpanId: string;
	/** Lets the browser tell whether it hydrates a document rendered for its own URL (TEC-3854). */
	url?: string;
}

interface SsrTraceStore {
	getStore(): SsrTrace | undefined;
}

/** Undefined outside an SSR render, which includes every call made from the browser. */
export function currentSsrTrace(): SsrTrace | undefined {
	return (globalThis as { __ssrTraceAls?: SsrTraceStore }).__ssrTraceAls?.getStore();
}

/**
 * On the server the in-flight request's path; in the browser the path the server baked into the HTML.
 * Not `location`, which is the point: see {@link documentReplayed}.
 */
export function currentDocumentUrl(): string | undefined {
	const fromRender = currentSsrTrace()?.url;
	if (fromRender) return fromRender;
	if (typeof window === 'undefined') return undefined;
	return window.__RENDER_URL || undefined;
}

/**
 * Routine for a crawler whose pipeline keys stored bodies on a normalised URL (TEC-3854). THREE states
 * (TEC-3881): `undefined` means it could not be asked, which lets `reportIsUnattributable` compare the
 * build instead.
 */
export function documentReplayed(): boolean | undefined {
	if (typeof window === 'undefined') return undefined;
	const rendered = currentDocumentUrl();
	if (!rendered) return undefined;
	return rendered !== window.location.pathname + window.location.search;
}

/**
 * Read back in the browser to keep `head()` isomorphic: an entry the server renders and the client does
 * not shifts every later one (#418 `args[]=HTML`). Undefined for the empty ids `head()` emits, which
 * callers re-emit rather than drop.
 */
export function currentDocumentTrace(): SsrTrace | undefined {
	const ssrTrace = currentSsrTrace();
	if (ssrTrace) return ssrTrace;
	if (typeof window === 'undefined') return undefined;
	const { __TRACE_ID: traceId, __ROOT_SPAN_ID: rootSpanId } = window;
	return traceId && rootSpanId ? { traceId, rootSpanId, url: window.__RENDER_URL || undefined } : undefined;
}
